import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import { userProfileQuery } from '@Admin/AdminUserProfilePage/query'

import Button from '~/core/Button'
import Divider from '~/core/Divider'
import { getUserIdentity } from '~/redux-modules/identity'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { useUserTeamsQuery } from '~/gql/graphql'

import { MoveQuoteOrderModal } from './MoveQuoteOrderModal'
import { ConfirmMoveQuoteOrderSuccessModal } from './ConfirmMoveQuoteOrderSuccessModal'

const MOVE_QUOTE = 'move-quote'
const BUTTON_TYPE = 'button'

type MoveQuoteProps = {
    id: string
    name: string
    userId?: number
    teamId?: number
    style?: { [key: string]: string }
    showDivider?: boolean
    type: 'button' | 'link'
    typeName?: 'Quote' | 'SalesOrder'
    placement: string
    ownerId?: number
    purchaserId?: number
}

export default function MoveQuoteOrder(props: MoveQuoteProps) {
    const {
        id,
        name,
        placement,
        type,
        typeName = 'Quote',
        style,
        showDivider = true,
        teamId,
        ownerId,
        purchaserId,
    } = props

    const [visible, setVisible] = useState<boolean>(false)
    const [confirmModalVisible, setConfirmModalVisible] =
        useState<boolean>(false)
    const [team, setTeam] = useState<{ id?: string; name?: string }>({})

    const isEarlyAccessTeamsEnabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.RELEASE_GET_EARLY_ACCESS_TEAMS,
    )
    const identity = useSelector(getUserIdentity)
    const currentUserId = identity.id

    const { userId: userOboId = '' } = useParams<{ userId: string }>()

    const userId = +userOboId || currentUserId

    const { data: userData } = useQuery(userProfileQuery, {
        variables: {
            id: userId,
        },
        skip: !userId,
    })

    const { data } = useUserTeamsQuery({
        variables: { userId },
    })

    const isCollaborator = ownerId !== userId

    const isPurchaser = purchaserId === userId

    const showMoveQuoteModal = () => {
        setVisible(true)
    }

    const handleCancel = (teamData?: { id: string; name?: string }) => {
        setVisible(false)
        if (teamData) {
            setTeam(teamData)
            setConfirmModalVisible(true)
        }
    }

    const userTeams = data?.user?.teams?.items || []

    const quoteInUsersTeam = userTeams.some(userTeam => userTeam.id === teamId)

    const isTeamsFeatureEnabled =
        userData?.user?.businessAccountMembership?.businessAccount
            ?.isTeamsFeatureEnabled

    const isTeamsDisabled =
        (!isTeamsFeatureEnabled && isEarlyAccessTeamsEnabled) ||
        (teamId && !quoteInUsersTeam) ||
        !id ||
        (isCollaborator && !quoteInUsersTeam && !isPurchaser)

    const buttonStyle =
        type === BUTTON_TYPE ? style : { padding: '4px 0px', ...style }

    return (
        <>
            {visible && (
                <MoveQuoteOrderModal
                    id={id}
                    typeName={typeName}
                    name={name}
                    visible={visible}
                    onCancel={handleCancel}
                    teams={userTeams}
                    userId={userId}
                    userTeamId={teamId}
                    isTeamsDisabled={isTeamsDisabled}
                />
            )}
            {confirmModalVisible && (
                <ConfirmMoveQuoteOrderSuccessModal
                    id={id}
                    typeName={typeName}
                    name={name}
                    team={team}
                    visible={confirmModalVisible}
                    onCancel={() => setConfirmModalVisible(false)}
                />
            )}
            {(isTeamsFeatureEnabled || isEarlyAccessTeamsEnabled) && (
                <>
                    <Button
                        name={MOVE_QUOTE}
                        data-test-target={`${MOVE_QUOTE}-button`}
                        placement={placement}
                        type={type === BUTTON_TYPE ? undefined : 'link'}
                        onClick={showMoveQuoteModal}
                        style={buttonStyle}
                    >
                        Add to...
                    </Button>
                    {showDivider && <Divider />}
                </>
            )}
        </>
    )
}
